import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, Input, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from "@angular/core";
import { roundToPrecision } from "@cf-shared/shared-util";
import { clamp } from 'lodash-es';

export interface CsSliderTickmark {
  value: number;
  label?: string;
}

export interface CsSliderBackgroundGradientMark {
  value: number;
  color: 'yellow' | 'green' | 'red';
}

function interpolateColor(color1, color2, ratio) {
  // Parse the CSS colors to RGB format
  const parseColor = (color) => {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 1;
    const ctx = canvas.getContext('2d');
    if (typeof ctx.fillStyle !== 'string') {
      throw new Error("Invalid color format or unsupported fillStyle.");
    }
    ctx.fillStyle = color;
    // Draw a 1x1 rectangle with the color, then read back the color data
    ctx.fillRect(0, 0, 1, 1);
    const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;
    return { r, g, b, a: a / 255 };  // Normalize alpha to 0-1
  };

  const c1 = parseColor(color1);
  const c2 = parseColor(color2);

  // Interpolate each color channel
  const r = Math.round(c1.r + (c2.r - c1.r) * ratio);
  const g = Math.round(c1.g + (c2.g - c1.g) * ratio);
  const b = Math.round(c1.b + (c2.b - c1.b) * ratio);

  // Convert back to CSS color string
  return `rgb(${r}, ${g}, ${b})`;
}

@Component({
  selector: 'cs-slider',
  host: { 'class': 'slider' },
  templateUrl: 'cs-slider.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CsSliderComponent {
  @Input() public min: number;
  @Input() public max: number;
  @Input()
  @HostBinding('attr.value') // Binds the property to the `value` attribute in the DOM
  public value: number;
  @Output() public valueChange = new EventEmitter<number>();
  @Input() highlightedValues: number[] = [];
  @Input() public step: number;
  @Input() public tickmarks: CsSliderTickmark[] = [];
  @Input() public disabled: boolean = false;
  @Input() public hideValues: boolean = false;
  @Input() public showPercentSign: boolean = true;
  @Input() public forceTickmarks: boolean = false;
  @Input() public customBackgroundGradientMarks: CsSliderBackgroundGradientMark[] = [];
  @ViewChildren('highlightedValueTickmark') highlightedValueTickmark: QueryList<ElementRef>;

  tickmarksChanged = true;

  allTickmarks: CsSliderTickmark[] = [];

  @ViewChild('tickmarksEl') tickmarksElementRef: ElementRef;
  @ViewChild('trackEl', { static: true }) trackElementRef: ElementRef;
  @ViewChild('thumbEl', { static: true }) thumbElementRef: ElementRef;
  @ViewChild('tooltipContent') tooltipContentRef: ElementRef;
  @ViewChild('tooltipArrow') tooltipArrowRef: ElementRef;

  dragging: boolean = false;

  Math = Math;

  mutationObserver: MutationObserver;

  constructor(private elementRef: ElementRef,
    private cdr: ChangeDetectorRef
  ) {
  }

  ngOnDestroy() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  filteredTickmarks() {
    return this.tickmarks.filter(tickmark => tickmark.value >= this.min && tickmark.value <= this.max);
  }

  getAllTickmarks() {
    if (this.tickmarks.length > 0) {
      return this.filteredTickmarks();

      // if step is defined, and reasonable (so it wouldn't create too many tickmarks) OR forceTickmarks is true
    } else if (typeof this.step === 'number' && (this.forceTickmarks || this.step > (this.max - this.min) / 10)) {
      //Create array with values in range min and max with step
      let tickmarks: CsSliderTickmark[] = [];
      for (let i = this.min; i <= this.max; i += this.step) {
        tickmarks.push({ value: i, label: `${i}` });
      }

      return tickmarks;
    } else {
      return [];
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.value && this.value !== undefined) {
      this.adjustSliderPosition();
      this.updateThumbColor();
    }

    if (changes.tickmarks || changes.min || changes.max) {
      this.tickmarksChanged = true;
      this.allTickmarks = this.getAllTickmarks();
      // Adjust tickmark positions when tickmarks change
      setTimeout(() => this.adjustTickmarksPositions(), 0);
      // Also adjust slider position when min/max change
      if ((changes.min || changes.max) && this.value !== undefined) {
        this.adjustSliderPosition();
      }
    }

    if (changes.customBackgroundGradientMarks) {
      this.updateCustomTrackBackground();
    }

    if (changes.highlightedValues && this.highlightedValueTickmark) {
      this.adjustHighlightedValuesTickmarksPositions();
    }

    this.cdr.markForCheck();
  }

  ngOnInit() {
    window.addEventListener('touchend', (event) => this.onDragEnd(event));
    window.addEventListener('mouseup', (event) => this.onDragEnd(event));
    window.addEventListener('touchmove', (event) => this.onDragMove(event));
    window.addEventListener('mousemove', (event) => this.onDragMove(event));

    // stop touch events from bubbling to parent swipe containers (e.g. cs-tab-content-container)
    // while dragging — the window-level listeners above fire too late to prevent parent handlers;
    // also handle the drag move here since stopPropagation prevents the window-level listener
    const el = this.elementRef.nativeElement as HTMLElement;
    el.addEventListener('touchmove', (event) => {
      if (this.dragging) {
        event.stopPropagation();
        this.handleThumbPositionChange(event);
      }
    }, { passive: false });

    if (this.value === undefined) {
      this.value = (this.min + this.max) / 2;
      if (typeof this.step === 'number' && this.step > 0) {
        this.value = roundToPrecision(this.value, this.step)
      }

      this.valueChange.emit(this.value);
    }

    this.adjustSliderPosition();
    this.updateCustomTrackBackground();
    this.updateThumbColor();

    this.allTickmarks = this.getAllTickmarks();
  }

  ngAfterViewInit() {
    if (this.highlightedValueTickmark) {
      this.highlightedValueTickmark.changes.subscribe(() => {
        this.adjustHighlightedValuesTickmarksPositions();
      });
    }
    this.adjustTickmarksPositions();
    this.adjustHighlightedValuesTickmarksPositions();
    // Ensure slider position is set correctly after view initialization
    this.adjustSliderPosition();
    this.updateThumbColor();
    // Use setTimeout to ensure all bindings are complete
    setTimeout(() => {
      this.adjustSliderPosition();
      this.updateThumbColor();
      this.cdr.markForCheck();
    }, 0);
    this.cdr.markForCheck();
  }

  private lastTickmarkAdjustment = { min: null, max: null, tickmarks: null };
  
  ngDoCheck() {
    // Only adjust if values actually changed and tickmarks element is available
    if (this.tickmarksElementRef && 
        (this.lastTickmarkAdjustment.min !== this.min || 
         this.lastTickmarkAdjustment.max !== this.max ||
         this.lastTickmarkAdjustment.tickmarks !== this.allTickmarks?.length)) {
      this.adjustTickmarksPositions();
      this.lastTickmarkAdjustment = { 
        min: this.min, 
        max: this.max, 
        tickmarks: this.allTickmarks?.length 
      };
    }
  }

  ngAfterContentInit() {
    let element = this.elementRef.nativeElement as HTMLElement;

    this.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'value') {
          if (element.hasAttribute('value')) {
            this.value = parseFloat(element.getAttribute('value'));
            this.valueChange.emit(this.value);
            this.adjustSliderPosition();
            this.updateThumbColor();
            this.updateHighlightedValuesTickmarksColors();
          }
        }
      });
    });

    this.mutationObserver.observe(element, {
      attributes: true // Observe attribute changes
    });

    this.cdr.markForCheck();
  }

  onDragEnd(ev: MouseEvent | TouchEvent) {
    if (!this.dragging) {
      return;
    }
    ev.preventDefault();
    ev.stopPropagation();
    this.dragging = false;
    const element = this.elementRef.nativeElement as HTMLElement;
    element.removeAttribute('dragging');
    //console.log("dragging ended")
  }

  adjustTickmarksPositions() {
    if (!this.tickmarksElementRef || !this.tickmarksElementRef.nativeElement) {
      return;
    }
    let tickmarksElement = this.tickmarksElementRef.nativeElement as HTMLElement;
    let allCheckmarks = this.allTickmarks;
    if (!allCheckmarks || allCheckmarks.length === 0) {
      return;
    }
    for (let iChild = 0; iChild < allCheckmarks.length; iChild++) {
      let child = tickmarksElement.children[iChild] as HTMLElement;
      if (child) {
        const percent = (allCheckmarks[iChild].value - this.min) / (this.max - this.min) * 100;
        child.style.setProperty('--tickmark-offset-percent', `${percent}%`);
        child.classList.toggle('first', percent === 0);
        child.classList.toggle('last', percent === 100);
      }
    }
  }

  adjustHighlightedValuesTickmarksPositions() {
    let highlightedValuesTickmarks = this.highlightedValueTickmark.toArray();
    for (let iChild = 0; iChild < highlightedValuesTickmarks.length; iChild++) {
      let child = highlightedValuesTickmarks[iChild].nativeElement as HTMLElement;
      child.style.setProperty('--tickmark-offset-percent', `${(this.highlightedValues[iChild] - this.min) / (this.max - this.min) * 100}%`);
    }

    this.updateHighlightedValuesTickmarksColors();
  }

  adjustSliderPosition() {
    // Ensure min and max are defined before calculating position
    if (this.min === undefined || this.max === undefined || this.value === undefined) {
      return;
    }
    
    let sliderValueRatio = (this.value - this.min) / (this.max - this.min);
    sliderValueRatio = Math.max(0, Math.min(1, sliderValueRatio)); // clamp the value between 0 and 1

    let element = this.elementRef.nativeElement as HTMLElement;
    element.style.setProperty('--slider-percent', `${sliderValueRatio * 100}%`);
  }

  private pendingDragUpdate = false;

  handleThumbPositionChange(ev: MouseEvent | TouchEvent) {
    ev.preventDefault();
    ev.stopPropagation();
    const clientX = ev instanceof MouseEvent ? ev.clientX : ev.touches[0].clientX;

    let elementBounds = this.elementRef.nativeElement.getBoundingClientRect();
    let sliderRatio = clamp((clientX - elementBounds.left) / elementBounds.width, 0, 1);
    let sliderRawValue = sliderRatio * (this.max - this.min) + this.min;

    let newValue = sliderRawValue;

    // if step is defined
    if (typeof this.step === 'number' && this.step > 0) {
      // Quantize the slider value to the nearest step
      let precision = Math.pow(10, -(-Math.floor(Math.log10(this.step))));
      let quantizedSliderValue = roundToPrecision(Math.round(sliderRatio * (this.max - this.min) / this.step) * this.step, precision);
      newValue = roundToPrecision(quantizedSliderValue + this.min, precision);
      if (this.highlightedValues.length > 0) {
        let closestHighlightedValue = newValue;
        for (let i = 0; i < this.highlightedValues.length; i++) {
          if (Math.abs(this.highlightedValues[i] - sliderRawValue) < Math.abs(closestHighlightedValue - sliderRawValue)) {
            closestHighlightedValue = this.highlightedValues[i];
          }
        }
        newValue = closestHighlightedValue;
      }
    }

    this.value = newValue;
    this.valueChange.emit(this.value);

    this.adjustSliderPosition();

    // Use requestAnimationFrame to batch drag updates for performance
    if (!this.pendingDragUpdate) {
      this.pendingDragUpdate = true;
      requestAnimationFrame(() => {
        this.cdr.markForCheck();
        this.pendingDragUpdate = false;
      });
    }
  }

  onDragMove(ev: MouseEvent | TouchEvent) {
    if (!this.dragging) {
      return;
    }

    this.handleThumbPositionChange(ev);
  }

  onDragStart(ev: MouseEvent | TouchEvent) {
    ev.preventDefault();
    ev.stopPropagation();
    if (this.disabled) {
      return;
    }
    this.dragging = true;
    const element = this.elementRef.nativeElement as HTMLElement;
    element.setAttribute('dragging', '');
    //console.log("dragging started")
  }

  onTrackClick(ev: MouseEvent | TouchEvent) {
    if (this.disabled) {
      return;
    }
    this.handleThumbPositionChange(ev);
  }

  updateCustomTrackBackground() {
    if (this.customBackgroundGradientMarks.length === 0) {
      return;
    }

    let trackElement = this.trackElementRef.nativeElement as HTMLElement;
    let gradientMarks = this.customBackgroundGradientMarks;
    let gradientString = gradientMarks.map(mark => `var(--${mark.color}) ${((mark.value - this.min) / (this.max - this.min)) * 100}%`).join(',');
    trackElement.style.setProperty('background', `linear-gradient(to right, ${gradientString})`);

    this.updateThumbColor();
    this.updateHighlightedValuesTickmarksColors();
  }

  findInterpolatedColorForValue(value: number, useHoverColors: boolean = false) {
    let element = this.elementRef.nativeElement as HTMLElement;
    let elementComputedStyle = getComputedStyle(element);
    let gradientMarks = this.customBackgroundGradientMarks;
    const colorSuffix = useHoverColors ? '-hover' : '';
    // Find the the gradient mark in which current value falls into
    let interpolatedColor = '';
    if (value >= gradientMarks[gradientMarks.length - 1].value) {
      interpolatedColor = elementComputedStyle.getPropertyValue(`--${gradientMarks[gradientMarks.length - 1].color}${colorSuffix}`).trim();
    } else if (value <= gradientMarks[0].value) {
      interpolatedColor = elementComputedStyle.getPropertyValue(`--${gradientMarks[0].color}${colorSuffix}`).trim();
    } else {
      for (let i = 1; i < gradientMarks.length; i++) {
        if (value >= gradientMarks[i - 1].value && value < gradientMarks[i].value) {
          const color1 = elementComputedStyle.getPropertyValue(`--${gradientMarks[i - 1].color}${colorSuffix}`);
          const color2 = elementComputedStyle.getPropertyValue(`--${gradientMarks[i].color}${colorSuffix}`);
          interpolatedColor = interpolateColor(color1, color2, (value - gradientMarks[i - 1].value) / (gradientMarks[i].value - gradientMarks[i - 1].value));
          break;
        }
      }
    }

    return interpolatedColor;
  }

  updateThumbColor() {
    if (this.customBackgroundGradientMarks.length === 0) {
      return;
    }

    const interpolatedColor = this.findInterpolatedColorForValue(this.value);
    const interpolatedHoverColor = this.findInterpolatedColorForValue(this.value, true);
    let thumbElement = this.thumbElementRef.nativeElement as HTMLElement;
    thumbElement.style.setProperty('--thumb-color', interpolatedColor);
    thumbElement.style.setProperty('--thumb-hover-color', interpolatedHoverColor);

    // also update tooltip colors via CSS variables
    if (this.tooltipContentRef) {
      let tooltipElement = this.tooltipContentRef.nativeElement as HTMLElement;
      tooltipElement.style.setProperty('--tooltip-color', interpolatedColor);
      tooltipElement.style.setProperty('--tooltip-hover-color', interpolatedHoverColor);
    }

    // also update tooltip arrow colors via CSS variables
    if (this.tooltipArrowRef) {
      let tooltipArrowElement = this.tooltipArrowRef.nativeElement as HTMLElement;
      tooltipArrowElement.style.setProperty('--tooltip-arrow-color', interpolatedColor);
      tooltipArrowElement.style.setProperty('--tooltip-arrow-hover-color', interpolatedHoverColor);
    }
  }

  updateHighlightedValuesTickmarksColors() {
    if (this.customBackgroundGradientMarks.length === 0 || !this.highlightedValueTickmark) {
      return;
    }

    let highlightedValuesTickmarks = this.highlightedValueTickmark.toArray();
    for (let iChild = 0; iChild < highlightedValuesTickmarks.length; iChild++) {
      let child = highlightedValuesTickmarks[iChild].nativeElement as HTMLElement;
      child.style.setProperty('--tickmark-color', this.findInterpolatedColorForValue(this.highlightedValues[iChild]));
    }
  }

}